import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import { ArrowRight, Check, Clock, Shield, Database, Zap, AlertTriangle } from 'lucide-react';

const TIMELINE = [
  { day: 'Day 0', title: 'Sign up free', desc: 'No credit card. 7-day trial unlocks the full platform.' },
  { day: 'Day 0', title: 'Drop your Botkeeper export', desc: 'CSV upload wizard auto-maps every column.' },
  { day: 'Day 0–1', title: 'AI re-categorizes everything', desc: 'GPT-5.2 + your firm policies replace whatever Botkeeper used.' },
  { day: 'Day 1–2', title: 'Reconnect bank feeds', desc: 'Plaid, QuickBooks Online, Xero — back online same day.' },
  { day: 'Day 2', title: 'Close your first month', desc: 'Recon Center, Exception Queue, and Close Readiness ready out of the box.' },
];

const KEEP_FEATURES = [
  'Per-client accounting policies',
  'Automated transaction categorization',
  'Vendor-account learning',
  'Bank feed integrations',
  'Multi-entity support',
  'Audit trail for every decision',
];

const BETTER_FEATURES = [
  { title: 'Hybrid AI you can trust', desc: 'GPT-5.2 reasoning + a deterministic rules engine + per-client context. Not a black box.' },
  { title: 'Close Controls in days, not weeks', desc: 'Recon Center, Exception Queue, Credit Card Controls — Botkeeper never shipped these.' },
  { title: 'Real-Time Alerts Center', desc: 'One feed for exceptions, missing docs, duplicates, and unusual spending.' },
  { title: 'You own your data, forever', desc: 'Export anything, anytime. We will never disappear on you.' },
];

export default function FromBotkeeperPage() {
  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }} data-testid="from-botkeeper-page">
      <PublicHeader />

      {/* HERO */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20 relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(217,138,74,0.12)', border: '1px solid rgba(217,138,74,0.25)' }}
              data-testid="urgency-pill"
            >
              <AlertTriangle size={13} style={{ color: '#D98A4A' }} />
              <span className="text-[12px] font-body font-semibold" style={{ color: '#D98A4A' }}>
                Botkeeper shut down February 7, 2026
              </span>
            </div>

            <h1
              className="font-heading font-semibold tracking-[-0.03em] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: '#0A3622', lineHeight: 0.95 }}
              data-testid="from-botkeeper-headline"
            >
              Your firm doesn't have to wait.<br />
              <span style={{ color: '#D98A4A' }}>Move to SnuggleMind in 48 hours.</span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-xl mb-8" style={{ color: '#4B5563' }}>
              Drop in your last Botkeeper export. Our AI re-categorizes every transaction, rebuilds your vendor list, and re-applies your firm's policies — usually before lunch.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/register?from=botkeeper"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all hover:scale-[1.02]"
                style={{ background: '#D98A4A', color: '#FFFFFF' }}
                data-testid="hero-cta-trial"
              >
                Start free migration <ArrowRight size={16} />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold"
                style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}
              >
                Why SnuggleMind
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] font-body" style={{ color: '#4B5563' }}>
              <span className="flex items-center gap-1.5"><Check size={14} style={{ color: '#2D7A5D' }} /> No credit card</span>
              <span className="flex items-center gap-1.5"><Check size={14} style={{ color: '#2D7A5D' }} /> 7-day full-access trial</span>
              <span className="flex items-center gap-1.5"><Check size={14} style={{ color: '#2D7A5D' }} /> White-glove migration support</span>
            </div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-md">
              <div className="rounded-3xl p-7" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB', boxShadow: '0 20px 60px -20px rgba(10,54,34,0.18)' }}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(217,138,74,0.12)' }}>
                    <Database size={22} style={{ color: '#D98A4A' }} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#D98A4A' }}>Migration in progress</p>
                    <p className="font-heading text-[15px] font-semibold" style={{ color: '#0A3622' }}>Your firm — Botkeeper export</p>
                  </div>
                </div>
                {[
                  { label: 'Transactions', count: '14,283', pct: 100 },
                  { label: 'Vendors', count: '1,247', pct: 100 },
                  { label: 'Chart of Accounts', count: '186', pct: 100 },
                  { label: 'AI re-categorization', count: '94% confident', pct: 94 },
                ].map((row) => (
                  <div key={row.label} className="mb-3.5 last:mb-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[12.5px] font-body font-medium" style={{ color: '#1A1A1A' }}>{row.label}</span>
                      <span className="text-[11px] font-mono" style={{ color: '#4B5563' }}>{row.count}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#F7F6F2' }}>
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${row.pct}%`, background: row.pct === 100 ? '#2D7A5D' : '#D98A4A' }} />
                    </div>
                  </div>
                ))}
                <div className="mt-5 pt-5 flex items-center gap-2" style={{ borderTop: '1px solid #E5E3DB' }}>
                  <Clock size={13} style={{ color: '#4B5563' }} />
                  <span className="text-[12px]" style={{ color: '#4B5563' }}>Estimated total time: <strong style={{ color: '#0A3622' }}>2 hours, 14 minutes</strong></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF / METRICS BAND */}
      <section className="py-12 border-y" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: '14,000+', l: 'Transactions migrated' },
            { v: '< 48hr', l: 'Average switch time' },
            { v: '94%', l: 'Auto-categorized on import' },
            { v: '0', l: 'Migration fees' },
          ].map((m) => (
            <div key={m.l} className="text-center">
              <p className="font-heading font-semibold tracking-tight" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: '#D98A4A', lineHeight: 1 }}>{m.v}</p>
              <p className="text-[13px] mt-1.5" style={{ color: '#4B5563' }}>{m.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>The 48-hour playbook</p>
            <h2 className="font-heading font-semibold tracking-tight" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#0A3622', lineHeight: 1.05 }}>
              From Botkeeper signed-off to closing the books — in two days.
            </h2>
          </div>

          <div className="space-y-1">
            {TIMELINE.map((t, i) => (
              <div key={t.title} className="flex items-start gap-5 py-5 transition-colors group hover:bg-white rounded-2xl px-5 -mx-5" data-testid={`timeline-step-${i}`}>
                <div className="flex-shrink-0 w-20 pt-1">
                  <span className="text-[11px] uppercase tracking-[0.18em] font-semibold" style={{ color: '#D98A4A' }}>{t.day}</span>
                </div>
                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5" style={{ background: '#0A3622' }}>
                  <span className="font-heading text-[13px] font-bold text-white">{i + 1}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-[18px] font-semibold tracking-tight mb-1" style={{ color: '#0A3622' }}>{t.title}</h3>
                  <p className="text-[14px] leading-relaxed" style={{ color: '#4B5563' }}>{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU KEEP / WHAT'S BETTER */}
      <section className="py-24" style={{ background: '#FFFFFF', borderTop: '1px solid #E5E3DB', borderBottom: '1px solid #E5E3DB' }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#2D7A5D' }}>What you keep</p>
            <h3 className="font-heading text-[26px] font-semibold tracking-tight mb-6" style={{ color: '#0A3622', lineHeight: 1.15 }}>
              Everything that worked.
            </h3>
            <ul className="space-y-3">
              {KEEP_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'rgba(45,122,93,0.12)' }}>
                    <Check size={13} style={{ color: '#2D7A5D' }} strokeWidth={2.5} />
                  </div>
                  <span className="text-[14.5px]" style={{ color: '#1A1A1A' }}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-4" style={{ color: '#D98A4A' }}>What's better</p>
            <h3 className="font-heading text-[26px] font-semibold tracking-tight mb-6" style={{ color: '#0A3622', lineHeight: 1.15 }}>
              Plus everything Botkeeper never shipped.
            </h3>
            <ul className="space-y-5">
              {BETTER_FEATURES.map((f) => (
                <li key={f.title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(217,138,74,0.12)' }}>
                    <Zap size={17} style={{ color: '#D98A4A' }} />
                  </div>
                  <div>
                    <p className="font-heading text-[15px] font-semibold mb-0.5" style={{ color: '#0A3622' }}>{f.title}</p>
                    <p className="text-[13.5px] leading-relaxed" style={{ color: '#4B5563' }}>{f.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(45,122,93,0.12)' }}>
            <Shield size={26} style={{ color: '#2D7A5D' }} strokeWidth={1.7} />
          </div>
          <h3 className="font-heading text-[24px] font-semibold tracking-tight mb-4" style={{ color: '#0A3622' }}>
            We're not going anywhere.
          </h3>
          <p className="text-[15.5px] leading-relaxed" style={{ color: '#4B5563' }}>
            Founded by accountants and engineers who watched Botkeeper's collapse from the front row.
            SnuggleMind is built on open standards, full data export at any time, and a contract that puts your firm first — not the platform.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" style={{ background: '#0A3622' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-semibold tracking-tight text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.05 }}>
            Don't wait for the next platform to disappear.
          </h2>
          <p className="text-[16px] mb-8 max-w-xl mx-auto" style={{ color: 'rgba(247,246,242,0.75)' }}>
            Start your free trial — Botkeeper migration is included at no charge through the end of Q1 2026.
          </p>
          <Link
            to="/register?from=botkeeper"
            className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all hover:scale-[1.02]"
            style={{ background: '#D98A4A', color: '#FFFFFF' }}
            data-testid="final-cta-trial"
          >
            Start free migration <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
